import { OUTCODES_WITH_REGIONS } from "../data/outcode-to-region-data";
import { z } from "zod";

// These names come from the data itself. They should potentially be renamed on parsing if wanted
export const GB_AVERAGE = "Great Britain average" as const;
export const ALL_POSTCODES = "All postcodes" as const;

export const REGIONS_WITHOUT_AVERAGE = [
  "North West",
  "Northern",
  "Yorkshire",
  "Northern Scotland",
  "Southern",
  "Southern Scotland",
  "North Wales and Mersey",
  "London",
  "South East",
  "Eastern",
  "East Midlands",
  "Midlands",
  "Southern Western",
  "South Wales",
] as const;

export const REGIONS = [...REGIONS_WITHOUT_AVERAGE, GB_AVERAGE] as const;

export const getRegionsWithoutAverage = () => REGIONS_WITHOUT_AVERAGE;
export const getRegions = () => REGIONS;

export type Region = (typeof REGIONS)[number];
export type RegionWithoutAverage = (typeof REGIONS_WITHOUT_AVERAGE)[number];
export type Outcode = keyof typeof OUTCODES_WITH_REGIONS;

export type PriceCapEntry = {
  standingChargePencePerDay: number;
  unitRatePencePerKwh: number;
};

export type PriceCapMap = {
  [K in Region]?: PriceCapEntry;
};

export type OutcodesWithRegions = Record<Outcode, Region>;
export type OutcodesWithRegionalPricing = Record<Outcode, PriceCapEntry>;

export const isRegion = (text: string): text is Region => {
  const textAsRegion = text as Region;
  return REGIONS.includes(textAsRegion);
};

export type PriceCapDbRow = {
  outcode: string;
  standingChargePencePerDay: number;
  unitRatePencePerKwh: number;
};

export type PriceCapData = Omit<PriceCapDbRow, "outcode">;

export type EnergyUsageDbRow = {
  outcode: string;
  postcode: string;
  meanConsumptionKwh: number;
};

const HALF_HOURLY_INCREMENTS = [
  "00:00:00",
  "00:30:00",
  "01:00:00",
  "01:30:00",
  "02:00:00",
  "02:30:00",
  "03:00:00",
  "03:30:00",
  "04:00:00",
  "04:30:00",
  "05:00:00",
  "05:30:00",
  "06:00:00",
  "06:30:00",
  "07:00:00",
  "07:30:00",
  "08:00:00",
  "08:30:00",
  "09:00:00",
  "09:30:00",
  "10:00:00",
  "10:30:00",
  "11:00:00",
  "11:30:00",
  "12:00:00",
  "12:30:00",
  "13:00:00",
  "13:30:00",
  "14:00:00",
  "14:30:00",
  "15:00:00",
  "15:30:00",
  "16:00:00",
  "16:30:00",
  "17:00:00",
  "17:30:00",
  "18:00:00",
  "18:30:00",
  "19:00:00",
  "19:30:00",
  "20:00:00",
  "20:30:00",
  "21:00:00",
  "21:30:00",
  "22:00:00",
  "22:30:00",
  "23:00:00",
  "23:30:00",
] as const;

export const getHalfHourlyIncrements = () => HALF_HOURLY_INCREMENTS;
export const HALF_HOURLY_SCHEMA = z.enum(getHalfHourlyIncrements());

export type HalfHourlyIncrement = (typeof HALF_HOURLY_INCREMENTS)[number];

export const isHalfHourlyIncrementBefore = (
  incrementA: HalfHourlyIncrement,
  incrementB: HalfHourlyIncrement,
) => {
  const [hoursA, minsA] = incrementA.split(":");
  const [hoursB, minsB] = incrementB.split(":");

  if (hoursA === hoursB) {
    return minsA < minsB;
  }

  return hoursA < hoursB;
};

const LIST_OF_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const getListOfMonths = () => LIST_OF_MONTHS;

export type Month = (typeof LIST_OF_MONTHS)[number];

export const MONTHS_IN_A_YEAR = Object.fromEntries(
  getListOfMonths().map((month, index) => [month, index]),
) as { [month in Month]: number };
